import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Plus, Send, Clock, Image, Hash, CheckCircle,
  AlertCircle, RefreshCw, Trash2, X, Edit3, ExternalLink,
  Facebook, Twitter, Linkedin, Instagram, Settings, Eye,
  ChevronLeft, ChevronRight, FileText, Loader2, Sparkles,
  Lightbulb
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  publishing: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  published: 'bg-green-500/20 text-green-400 border-green-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const PLATFORM_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
};

const PLATFORM_COLORS = {
  facebook: 'bg-blue-600',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
  linkedin: 'bg-blue-700',
  twitter: 'bg-sky-500',
};

const SocialMedia = () => {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [createForm, setCreateForm] = useState({
    content: '',
    platforms: [],
    media_urls: [],
    hashtags: [],
    scheduled_at: ''
  });
  const [hashtagInput, setHashtagInput] = useState('');
  const [publishing, setPublishing] = useState(null);
  const [showAIInput, setShowAIInput] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [postsRes, statsRes, credsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/social`, { headers }),
        axios.get(`${BACKEND_URL}/api/social/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/social/credentials/status`, { headers }),
      ]);

      setPosts(postsRes.data);
      setStats(statsRes.data);
      setCredentials(credsRes.data);
    } catch (error) {
      console.error('Failed to fetch social data:', error);
      toast.error('Failed to load social media data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalendar = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      
      const res = await axios.get(
        `${BACKEND_URL}/api/social/calendar?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalendarData(res.data);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendar();
    }
  }, [activeTab, fetchCalendar]);

  const handleCreatePost = async (e, isDraft = false) => {
    e.preventDefault();
    if (createForm.platforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        ...createForm,
        scheduled_at: isDraft ? null : createForm.scheduled_at || null
      };
      
      await axios.post(
        `${BACKEND_URL}/api/social`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(isDraft ? 'Post saved as draft' : 'Post scheduled');
      setShowCreateModal(false);
      setCreateForm({ content: '', platforms: [], media_urls: [], hashtags: [], scheduled_at: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    }
  };

  const handlePublishNow = async (postId) => {
    setPublishing(postId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/social/${postId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.status === 'published') {
        toast.success('Post published successfully!');
      } else if (res.data.status === 'failed') {
        toast.error('Publishing failed. Check platform credentials.');
      } else {
        toast.warning('Post partially published');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(null);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${BACKEND_URL}/api/social/${postId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleConnectPlatform = async (platform) => {
    try {
      const token = localStorage.getItem('auth_token');
      
      // Try to get OAuth URL for live connection
      const response = await axios.get(
        `${BACKEND_URL}/api/social/${platform}/connect`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.auth_url) {
        // Redirect to OAuth provider
        window.location.href = response.data.auth_url;
      } else {
        toast.info('OAuth not configured. Using demo mode.');
        // Fall back to demo mode
        await axios.post(
          `${BACKEND_URL}/api/social/credentials`,
          {
            platform,
            credentials: {
              account_name: `Demo ${platform.charAt(0).toUpperCase() + platform.slice(1)} Account`,
              access_token: 'demo_token_placeholder'
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${platform} connected (demo mode)`);
        fetchData();
      }
    } catch (error) {
      // If OAuth endpoints not available, use demo mode
      if (error.response?.status === 400) {
        toast.warning(error.response.data.detail || 'OAuth not configured');
      } else {
        // Fall back to demo mode
        try {
          const token = localStorage.getItem('auth_token');
          await axios.post(
            `${BACKEND_URL}/api/social/credentials`,
            {
              platform,
              credentials: {
                account_name: `Demo ${platform.charAt(0).toUpperCase() + platform.slice(1)} Account`,
                access_token: 'demo_token_placeholder'
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success(`${platform} connected (demo mode)`);
          fetchData();
        } catch (demoError) {
          toast.error('Failed to connect platform');
        }
      }
    }
  };

  const handleDisconnectPlatform = async (platform) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${BACKEND_URL}/api/social/credentials/${platform}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${platform} disconnected`);
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect platform');
    }
  };

  const togglePlatform = (platform) => {
    const current = createForm.platforms;
    if (current.includes(platform)) {
      setCreateForm({ ...createForm, platforms: current.filter(p => p !== platform) });
    } else {
      setCreateForm({ ...createForm, platforms: [...current, platform] });
    }
  };

  const addHashtag = () => {
    if (hashtagInput.trim()) {
      const tag = hashtagInput.startsWith('#') ? hashtagInput : `#${hashtagInput}`;
      if (!createForm.hashtags.includes(tag)) {
        setCreateForm({ ...createForm, hashtags: [...createForm.hashtags, tag] });
      }
      setHashtagInput('');
    }
  };

  const removeHashtag = (tag) => {
    setCreateForm({ ...createForm, hashtags: createForm.hashtags.filter(t => t !== tag) });
  };

  // AI Generate Caption & Hashtags
  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    
    setGeneratingAI(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/social/generate-caption`,
        { topic: aiTopic },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { caption, hashtags } = response.data;
      setCreateForm({
        ...createForm,
        content: caption,
        hashtags: hashtags || []
      });
      setShowAIInput(false);
      setAiTopic('');
      toast.success('Caption generated!');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate caption');
    } finally {
      setGeneratingAI(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-[#052e22]/30 rounded-lg" />);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendarData.find(d => d.date === dateStr);
      const postsForDay = dayData?.posts || [];
      const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
      
      days.push(
        <div
          key={day}
          className={`h-24 p-2 rounded-lg border transition-colors ${
            isToday ? 'border-emerald-500 bg-emerald-900/20' : 'border-emerald-800/30 bg-[#052e22]/50'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className={`text-sm font-medium ${isToday ? 'text-emerald-400' : 'text-emerald-300/70'}`}>
              {day}
            </span>
            {postsForDay.length > 0 && (
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 rounded">
                {postsForDay.length}
              </span>
            )}
          </div>
          <div className="space-y-1 overflow-hidden">
            {postsForDay.slice(0, 2).map(post => (
              <div
                key={post.id}
                className="text-xs text-emerald-300/70 truncate bg-[#0a3d2e] px-1.5 py-0.5 rounded"
              >
                {post.content_preview}
              </div>
            ))}
            {postsForDay.length > 2 && (
              <div className="text-xs text-emerald-400">+{postsForDay.length - 2} more</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div data-testid="social-media-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-pink-400" />
            Social Media
          </h1>
          <p className="text-emerald-300/70">Schedule and publish posts across platforms</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowSettingsModal(true)}
            variant="outline"
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/30"
          >
            <Settings className="w-4 h-4 mr-2" />
            Platforms
          </Button>
          <Button
            data-testid="create-post-btn"
            onClick={() => setShowCreateModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_posts}</p>
                  <p className="text-xs text-emerald-300/70">Total Posts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.scheduled_posts}</p>
                  <p className="text-xs text-emerald-300/70">Scheduled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.published_posts}</p>
                  <p className="text-xs text-emerald-300/70">Published</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-500/20 rounded-lg">
                  <Edit3 className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.draft_posts}</p>
                  <p className="text-xs text-emerald-300/70">Drafts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.failed_posts}</p>
                  <p className="text-xs text-emerald-300/70">Failed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['posts', 'calendar'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium transition-all capitalize ${
              activeTab === tab
                ? 'bg-emerald-600 text-white'
                : 'bg-[#052e22]/50 text-emerald-300/70 hover:bg-emerald-900/30'
            }`}
          >
            {tab === 'calendar' && <Calendar className="w-4 h-4 inline mr-2" />}
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'posts' && (
          <motion.div
            key="posts"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-white">All Posts</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchData}
                    className="text-emerald-400 hover:bg-emerald-900/30"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
                    <p className="text-emerald-300/50">No posts yet</p>
                    <p className="text-emerald-300/30 text-sm mt-1">Create your first social media post</p>
                  </div>
                ) : (
                  <div className="divide-y divide-emerald-800/30">
                    {posts.map((post) => (
                      <div
                        key={post.id}
                        className="flex items-start justify-between p-4 hover:bg-emerald-900/20 transition-colors"
                      >
                        <div className="flex-1 min-w-0 mr-4">
                          <p className="text-white mb-2">{post.content_preview}</p>
                          <div className="flex items-center gap-3">
                            <div className="flex gap-1">
                              {post.platforms.map(platform => {
                                const Icon = PLATFORM_ICONS[platform];
                                return (
                                  <div
                                    key={platform}
                                    className={`w-6 h-6 rounded flex items-center justify-center ${PLATFORM_COLORS[platform]}`}
                                  >
                                    <Icon className="w-3 h-3 text-white" />
                                  </div>
                                );
                              })}
                            </div>
                            <Badge className={STATUS_COLORS[post.status]}>{post.status}</Badge>
                            {post.scheduled_at && (
                              <span className="text-xs text-emerald-300/50">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {formatDate(post.scheduled_at)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {(post.status === 'draft' || post.status === 'scheduled') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePublishNow(post.id)}
                              disabled={publishing === post.id}
                              className="text-emerald-400 hover:bg-emerald-900/30"
                            >
                              {publishing === post.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePost(post.id)}
                            className="text-red-400 hover:bg-red-900/30"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {activeTab === 'calendar' && (
          <motion.div
            key="calendar"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      className="text-emerald-400"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <CardTitle className="text-lg font-semibold text-white">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      className="text-emerald-400"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(new Date())}
                    className="text-emerald-400 hover:bg-emerald-900/30"
                  >
                    Today
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-emerald-300/70 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {renderCalendar()}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Post Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Create Post</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>
              <form onSubmit={(e) => handleCreatePost(e, false)} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-emerald-300">Content *</label>
                    <Button
                      type="button"
                      onClick={() => setShowAIInput(!showAIInput)}
                      variant="ghost"
                      size="sm"
                      className="text-purple-400 hover:text-purple-300 hover:bg-purple-900/30"
                      data-testid="ai-generate-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      AI Generate
                    </Button>
                  </div>
                  
                  {/* AI Generate Input */}
                  <AnimatePresence>
                    {showAIInput && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mb-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg"
                      >
                        <p className="text-sm text-purple-300 mb-2">What is your post about?</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={aiTopic}
                            onChange={(e) => setAiTopic(e.target.value)}
                            placeholder="e.g., DevOps course launch"
                            className="flex-1 px-3 py-2 bg-[#052e22] border border-purple-500/50 rounded-lg text-white placeholder-purple-400/50 focus:outline-none focus:border-purple-500"
                            data-testid="ai-topic-input"
                          />
                          <Button
                            type="button"
                            onClick={handleAIGenerate}
                            disabled={generatingAI}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            data-testid="generate-caption-btn"
                          >
                            {generatingAI ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Generate'
                            )}
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  <textarea
                    required
                    value={createForm.content}
                    onChange={(e) => setCreateForm({ ...createForm, content: e.target.value })}
                    placeholder="What would you like to share?"
                    rows={4}
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500 resize-none"
                  />
                  <p className="text-xs text-emerald-300/50 mt-1 text-right">{createForm.content.length} characters</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Platforms *</label>
                  <div className="flex gap-2">
                    {Object.entries(PLATFORM_ICONS).map(([platform, Icon]) => (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => togglePlatform(platform)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                          createForm.platforms.includes(platform)
                            ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400'
                            : 'border-emerald-800/30 text-emerald-300/50 hover:border-emerald-600'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm capitalize">{platform}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Hashtags</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={hashtagInput}
                      onChange={(e) => setHashtagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                      placeholder="Add hashtag..."
                      className="flex-1 px-4 py-2 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500"
                    />
                    <Button type="button" onClick={addHashtag} variant="outline" className="border-emerald-500/50 text-emerald-400">
                      <Hash className="w-4 h-4" />
                    </Button>
                  </div>
                  {createForm.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {createForm.hashtags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-sm"
                        >
                          {tag}
                          <button type="button" onClick={() => removeHashtag(tag)} className="hover:text-red-400">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Schedule (optional)</label>
                  <input
                    type="datetime-local"
                    value={createForm.scheduled_at}
                    onChange={(e) => setCreateForm({ ...createForm, scheduled_at: e.target.value })}
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  />
                  {/* Best Time Suggestion */}
                  <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="best-time-suggestion">
                    <p className="text-sm text-amber-400 flex items-center gap-1 mb-2">
                      <Lightbulb className="w-4 h-4" />
                      Best time to post:
                    </p>
                    <div className="grid grid-cols-2 gap-1 text-xs text-amber-300/80">
                      <span><Instagram className="w-3 h-3 inline mr-1" />Instagram: 6-8 PM</span>
                      <span><Linkedin className="w-3 h-3 inline mr-1" />LinkedIn: 9-11 AM</span>
                      <span><Facebook className="w-3 h-3 inline mr-1" />Facebook: 12-3 PM</span>
                      <span><Twitter className="w-3 h-3 inline mr-1" />Twitter: 5-7 PM</span>
                    </div>
                  </div>
                </div>

                {/* Post Preview Section */}
                <AnimatePresence>
                  {showPreview && createForm.content && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3"
                      data-testid="post-preview"
                    >
                      <p className="text-sm font-medium text-emerald-300">Post Preview</p>
                      
                      {/* Instagram Preview */}
                      <div className="bg-white rounded-lg p-3 text-black">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                            <Instagram className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold text-sm">your_account</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{createForm.content}</p>
                        {createForm.hashtags.length > 0 && (
                          <p className="text-blue-600 text-sm mt-1">{createForm.hashtags.join(' ')}</p>
                        )}
                      </div>
                      
                      {/* LinkedIn Preview */}
                      <div className="bg-white rounded-lg p-3 text-black">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-blue-700 rounded flex items-center justify-center">
                            <Linkedin className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <span className="font-semibold text-sm block">Your Name</span>
                            <span className="text-xs text-gray-500">Marketing Professional</span>
                          </div>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{createForm.content}</p>
                        {createForm.hashtags.length > 0 && (
                          <p className="text-blue-600 text-sm mt-1">{createForm.hashtags.join(' ')}</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-900/30"
                    data-testid="preview-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {showPreview ? 'Hide Preview' : 'Preview'}
                  </Button>
                  <Button
                    type="button"
                    onClick={(e) => handleCreatePost(e, true)}
                    variant="outline"
                    className="flex-1 border-emerald-500/50 text-emerald-400"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Save Draft
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {createForm.scheduled_at ? 'Schedule' : 'Create'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Platform Settings Modal */}
      <AnimatePresence>
        {showSettingsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSettingsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Platform Connections</h2>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>
              <p className="text-sm text-emerald-300/70 mb-4">
                Connect your social media accounts to publish posts. (Demo mode - actual API credentials required for production)
              </p>
              <div className="space-y-3">
                {credentials.map(cred => {
                  const Icon = PLATFORM_ICONS[cred.platform];
                  return (
                    <div
                      key={cred.platform}
                      className="flex items-center justify-between p-4 bg-[#052e22] rounded-lg border border-emerald-800/30"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${PLATFORM_COLORS[cred.platform]}`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-medium capitalize">{cred.platform}</p>
                          <p className="text-xs text-emerald-300/50">
                            {cred.is_configured ? cred.account_name : 'Not connected'}
                          </p>
                        </div>
                      </div>
                      {cred.is_configured ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnectPlatform(cred.platform)}
                          className="text-red-400 hover:bg-red-900/30"
                        >
                          Disconnect
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleConnectPlatform(cred.platform)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-emerald-300/50 mt-4 text-center">
                Actual API credentials will be configured later. Posts are simulated in demo mode.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SocialMedia;
